/** Resolves a BannerImageSource ({kind, id} | null) to an actual URL. */
function resolveImageSource(source, references) {
  if (!source) return null;
  if (source.kind === "reference") {
    return (references ?? []).find((r) => r.id === source.id)?.url ?? null;
  }
  // "generated" / "library" — not reachable yet, AI generation is stubbed.
  return null;
}

// One cached canvas 2D context, reused across every measureText call rather
// than recreated per call - this runs on every drag frame during live
// text-fit recomputation, so per-call canvas creation would be wasteful.
let measureCtx = null;
function getMeasureCtx() {
  if (!measureCtx && typeof document !== "undefined") {
    measureCtx = document.createElement("canvas").getContext("2d");
  }
  return measureCtx;
}

/** Builds the ctx object renderBanner/renderBannerSrcdoc need. */
export function buildRenderContext(doc, references) {
  // Same "BrandFont" family name buildEmissions.mjs uses for HTML5 export,
  // so the live preview and an actual export render with the same font
  // rather than preview silently falling back to the system stack.
  const fontRef = (references ?? []).find((r) => r.kind === "font");
  const fontFace = fontRef ? { family: "BrandFont", url: fontRef.url } : null;
  const fontFamily = fontFace
    ? `'${fontFace.family}',system-ui,-apple-system,'Segoe UI',Arial,sans-serif`
    : `system-ui,-apple-system,'Segoe UI',Arial,sans-serif`;

  return {
    fontFace,
    // Every image/logo resolution goes through here, keyed on the actual
    // per-size cascaded source - never pre-resolve a single "the" image or
    // logo URL from the master slots alone, since a size's own override
    // must be able to render something different.
    urlFor: (source) => resolveImageSource(source, references),
    // Real Canvas text measurement for the live text-fit algorithm - exact,
    // unlike the character-width heuristic Node falls back to (no
    // Canvas/DOM there). Returns 0 if canvas is unavailable (e.g. SSR)
    // rather than throwing, so callers can fall back gracefully.
    measureText: (text, fontSize, fontWeight = 400) => {
      const c = getMeasureCtx();
      if (!c) return 0;
      c.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
      return c.measureText(text).width;
    },
  };
}